export interface CancunCrownProps {
  className?: string;
}

/**
 * CORONA DE `/cancun` — local a la página, y con motivo.
 *
 * `VipCrown` del substrato **está denegado a todos** (orden del ORACLE,
 * 26-jul-2026: lo importan Bogotá, Miami y `shared/BriefBlocks` además de esta
 * página, así que un cambio allí sale por cuatro sitios). Y aquí hacía falta
 * otro dibujo, no otro tamaño: ampliado 5× sobre `DEMO_5_CancunVIP.png` y sobre
 * el render, **no son la misma corona**.
 *
 * | | Póster | `VipCrown` |
 * |---|---|---|
 * | puntas | **5** | 3 |
 * | remates | 4 perlas + **almendra apuntada** en la del centro | 3 perlas |
 * | aro de base | **sí**, dos filetes capilares en lente | no lo hay |
 * | cuerpo | relleno con un **calado** bajo la punta central | macizo |
 * | proporción | 71 × 36 px (1,97) | 48 × 32 (1,50) |
 *
 * Y esto no es un capricho de detalle: ésta es la página del aire y del filete
 * capilar. Una corona maciza de tres puntas es el registro RUIDOSO — justo lo
 * que el encargo prohíbe («no la hagas más ruidosa que las otras: hazla más
 * fina»). El aro de base y el calado son lo que la vuelven fina.
 *
 * GEOMETRÍA MEDIDA, no dibujada a ojo. Ampliando el póster 5× y devolviendo las
 * coordenadas a la caja de tinta (x 470..540, y 151..187 del PNG):
 *   ápices en x = 3 · 17 · **36** · 55 · 69 de 72 (centro en el eje)
 *   alturas   y = 13 · 9 · **5** · 9 · 13  (las exteriores caen 8 unidades)
 *   valles    y = 21 · 20,5 · 20,5 · 21
 *   aro       y = 24 (canto superior) .. 38 (inferior), las dos curvas ARQUEADAS
 *             HACIA ABAJO: el aro es una elipse y se le ve el canto delantero.
 *
 * El tamaño lo pone el CSS del módulo, no un atributo `size`: tiene que escalar
 * con el viewport (7,0 % del ancho, medido) y un `width` en el atributo es un
 * atributo de presentación que pisa cualquier regla. Por lo mismo el módulo
 * declara `max-width: none`, porque el reset global pone `svg { max-width: 100% }`.
 */
export function CancunCrown({ className }: CancunCrownProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 72 40"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      {/* Cuerpo de cinco puntas. El `fill-rule="evenodd"` abre el calado del
          centro sin pintar crema encima: la corona se apoya sobre la fotografía
          del hero y un relleno opaco «del color del fondo» sería mentira en
          cuanto la foto cambiara un tono. */}
      <path
        fill="currentColor"
        fillOpacity="0.94"
        fillRule="evenodd"
        d="M3 13 L10 21 L17 9 L26 20.5 L36 5 L46 20.5 L55 9 L62 21 L69 13 L67 24 Q36 32 5 24 Z
           M36 12.5 L37.6 15.5 L36 18.5 L34.4 15.5 Z"
      />

      {/* Remate de la punta central: almendra apuntada, no perla. */}
      <path
        fill="currentColor"
        d="M36 0.5 C 37.9 3.4, 37.9 5.6, 36 7.4 C 34.1 5.6, 34.1 3.4, 36 0.5 Z"
      />

      {/* Las cuatro perlas. Las exteriores son un pelo mayores, como el póster. */}
      <circle cx="3" cy="12.5" r="2.3" fill="currentColor" />
      <circle cx="17" cy="8" r="2.2" fill="currentColor" />
      <circle cx="55" cy="8" r="2.2" fill="currentColor" />
      <circle cx="69" cy="12.5" r="2.3" fill="currentColor" />

      {/* Aro de base: DOS filetes capilares, no una banda rellena. Es el rasgo
          que distingue esta corona de la del substrato y el que la deja en el
          registro fino de la página. `non-scaling-stroke` para que no engorden
          al escalar el SVG con el viewport. */}
      <path
        d="M5 24 Q36 32 67 24"
        stroke="currentColor"
        strokeWidth="1.1"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d="M8 29 Q36 38 64 29"
        stroke="currentColor"
        strokeWidth="1.1"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
